import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
    QrCode,
    Receipt,
    CheckCircle,
    PointOfSale,
    Add,
    Close,
    Refresh
} from '@mui/icons-material';
import DashboardHeader from "../../components/admin/DashboardHeader";
import AdminSidebar from "../../components/admin/AdminSidebar";
import { useAuth } from "../../contexts/AuthContext";
import { fetchCompanyPos, formatTerminalOptionLabel } from "../../services/posService";
import { createPaymentRequest, extractPaymentRequestId } from "../../services/paymentRequestService";
import { parseAmount, normalizeAmountForApi, formatAmountInputDisplay } from "../../utils/amount";
import AmountInput from "../../components/common/AmountInput";
import { fetchProviders } from "../../services/providerService";
import { PAYMENT_REQUEST_VALIDITY_MINUTES } from "../../config/environment";
import WaitingPaymentModal from "../../components/payment/WaitingPaymentModal";
import ProviderPicker from "../../components/payment/ProviderPicker";
import PaymentRequestAside from "../../components/payment/PaymentRequestAside";

// Modal pour gérer les terminaux
const TerminalManagementModal = ({ show, onClose, posList }) => {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [terminalForm, setTerminalForm] = useState({
        name: '',
        location: '',
        type: 'mobile',
        merchantId: ''
    });

    const handleCreateTerminal = (e) => {
        e.preventDefault();
        // Cette fonctionnalité n'est pas implémentée pour le moment
        // Les terminaux sont créés via la page de gestion des caisses
        alert('La création de terminaux se fait via la page de gestion des caisses.');
        setTerminalForm({ name: '', location: '', type: 'mobile', merchantId: '' });
        setShowCreateForm(false);
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50/80">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                            <PointOfSale className="text-amber-600 text-xl" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-slate-800">Gestion des Terminaux</h3>
                            <p className="text-xs text-slate-500">Configurez vos terminaux de paiement</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-200/80 rounded-xl transition-colors duration-200 text-slate-500 hover:text-slate-700"
                    >
                        <Close className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <div>
                            <h4 className="text-lg font-semibold text-slate-900">Terminaux Disponibles</h4>
                                    <p className="text-sm text-slate-600">{(posList || []).length} POS disponible(s)</p>
                        </div>
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-sm hover:shadow-md"
                        >
                            <Add className="text-lg" />
                            <span className="font-medium">Nouveau Terminal</span>
                        </button>
                    </div>

                    {showCreateForm && (
                        <div className="bg-slate-50 rounded-2xl p-5 mb-6 border border-slate-200">
                            <div className="flex items-center justify-between mb-4">
                                <h5 className="text-md font-semibold text-slate-900">Configuration du Terminal</h5>
                                <button
                                    onClick={() => setShowCreateForm(false)}
                                    className="p-1 hover:bg-slate-200 rounded-lg transition-colors"
                                >
                                    <Close className="text-slate-500 text-sm" />
                                </button>
                            </div>
                            <form onSubmit={handleCreateTerminal} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Nom du Terminal *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={terminalForm.name}
                                            onChange={(e) => setTerminalForm(prev => ({...prev, name: e.target.value}))}
                                            className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                                            placeholder="Ex: Terminal Principal"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Localisation *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={terminalForm.location}
                                            onChange={(e) => setTerminalForm(prev => ({...prev, location: e.target.value}))}
                                            className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                                            placeholder="Ex: Caisse 1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Type de Terminal *
                                        </label>
                                        <select
                                            value={terminalForm.type}
                                            onChange={(e) => setTerminalForm(prev => ({...prev, type: e.target.value}))}
                                            className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                                        >
                                            <option value="mobile">Mobile</option>
                                            <option value="fixed">Fixe</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            ID du Marchand
                                        </label>
                                        <input
                                            type="text"
                                            value={terminalForm.merchantId}
                                            onChange={(e) => setTerminalForm(prev => ({...prev, merchantId: e.target.value}))}
                                            className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                                            placeholder="Optionnel"
                                        />
                                    </div>
                                </div>
                                <div className="flex space-x-3 pt-2">
                                    <button
                                        type="submit"
                                        className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 font-medium shadow-sm hover:shadow-md"
                                    >
                                        Créer le Terminal
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateForm(false)}
                                        className="px-6 py-2.5 bg-slate-500 text-white rounded-xl hover:bg-slate-600 transition-all duration-300 font-medium"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    <div className="space-y-3">
                        {(posList || []).map(pos => (
                            <div key={pos.id} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-2xl hover:shadow-md transition-all duration-200">
                                <div className="flex items-center space-x-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-sm">
                                        <PointOfSale className="text-white text-lg" />
                                    </div>
                                    <div>
                                        <h5 className="font-semibold text-slate-900">{pos.name}</h5>
                                        <p className="text-sm text-slate-600">{pos.description || 'POS'} • {pos.tag || 'N/A'}</p>
                                        {pos.createdAt && (
                                            <p className="text-xs text-slate-500">Créé le {new Date(pos.createdAt).toLocaleDateString('fr-FR')}</p>
                                        )}
                                    </div>
                                </div>
                                <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                                    🟢 Actif
                                </span>
                            </div>
                        ))}
                    </div>

                    {(posList || []).length === 0 && !showCreateForm && (
                        <div className="text-center py-12">
                            <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <PointOfSale className="text-slate-400 text-3xl" />
                            </div>
                            <h4 className="text-lg font-semibold text-slate-500 mb-2">Aucun terminal configuré</h4>
                            <p className="text-slate-400 mb-6">Créez votre premier terminal pour commencer à accepter les paiements</p>
                            <button
                                onClick={() => setShowCreateForm(true)}
                                className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 font-medium shadow-sm hover:shadow-md"
                            >
                                <Add className="inline mr-2" />
                                Créer un Terminal
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const pickTerminalFromList = (list, preferId = null) => {
    if (!list?.length) return null;
    if (preferId) {
        const match = list.find((p) => p.id === preferId);
        if (match) return match;
    }
    return list[0];
};

const PaymentRequests = () => {
    const { selectedOrganization } = useAuth();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [showTerminalModal, setShowTerminalModal] = useState(false);
    const [selectedPos, setSelectedPos] = useState(null);
    const [posList, setPosList] = useState([]);
    const [loadingTerminals, setLoadingTerminals] = useState(false);
    const [terminalsError, setTerminalsError] = useState('');

    // Fonction utilitaire pour formater la devise
    const formatCurrency = (amount, currency) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: currency || 'CDF'
        }).format(amount);
    };

    // États pour le formulaire de création
    const [title, setTitle] = useState('Règlement');
    const [amount, setAmount] = useState('');
    const [formCurrency, setFormCurrency] = useState('CDF');
    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [account, setAccount] = useState('');
    const [accountFormatError, setAccountFormatError] = useState(false);
    const [loadingProviders, setLoadingProviders] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [waitingModalOpen, setWaitingModalOpen] = useState(false);
    const [waitingRequestId, setWaitingRequestId] = useState(null);
    const accountRef = useRef(null);
    const previousValue = useRef({});

    const loadPosList = useCallback(async (preferTerminalId = null) => {
        const orgId = selectedOrganization?.id;
        if (!orgId) {
            setPosList([]);
            setSelectedPos(null);
            return [];
        }
        setLoadingTerminals(true);
        setTerminalsError('');
        try {
            const list = await fetchCompanyPos(orgId);
            setPosList(list);
            setSelectedPos((prev) => pickTerminalFromList(list, preferTerminalId || prev?.id));
            return list;
        } catch (error) {
            console.error('Error loading POS list:', error);
            setPosList([]);
            setSelectedPos(null);
            setTerminalsError(error.message || 'Impossible de charger les terminaux.');
            return [];
        } finally {
            setLoadingTerminals(false);
        }
    }, [selectedOrganization?.id]);

    useEffect(() => {
        if (selectedOrganization?.id) {
            loadPosList();
            loadProviders();
        }
    }, [selectedOrganization?.id, loadPosList]);

    useEffect(() => {
        if (selectedOrganization?.id && location.pathname === '/admin/payment-requests') {
            loadPosList();
        }
    }, [location.pathname, selectedOrganization?.id, loadPosList]);

    useEffect(() => {
        const onPosChanged = (event) => {
            const orgId = event.detail?.organizationId;
            if (orgId && orgId !== selectedOrganization?.id) return;
            loadPosList(event.detail?.terminalId || null);
        };
        window.addEventListener('pos:created', onPosChanged);
        window.addEventListener('pos:updated', onPosChanged);
        return () => {
            window.removeEventListener('pos:created', onPosChanged);
            window.removeEventListener('pos:updated', onPosChanged);
        };
    }, [selectedOrganization?.id, loadPosList]);

    useEffect(() => {
        const refreshIfVisible = () => {
            if (document.visibilityState === 'visible' && selectedOrganization?.id) {
                loadPosList(selectedPos?.id || null);
            }
        };
        document.addEventListener('visibilitychange', refreshIfVisible);
        window.addEventListener('focus', refreshIfVisible);
        return () => {
            document.removeEventListener('visibilitychange', refreshIfVisible);
            window.removeEventListener('focus', refreshIfVisible);
        };
    }, [selectedOrganization?.id, loadPosList, selectedPos?.id]);

    useEffect(() => {
        if (selectedProvider?.id) {
            setAccount('');
            setAccountFormatError(false);
            previousValue.current = '';
        }
    }, [selectedProvider?.id]);

    useEffect(() => {
        if (account && selectedProvider) {
            const accountLength = selectedProvider.account_length;
            const accountFormat = selectedProvider.account_format;
            
            if (account.length === accountLength && accountFormat) {
                try {
                    const regex = new RegExp(accountFormat);
                    if (!regex.test(account)) {
                        setAccountFormatError(true);
                    } else {
                        setAccountFormatError(false);
                    }
                } catch {
                    setAccountFormatError(false);
                }
            } else {
                setAccountFormatError(false);
            }
        }
    }, [account, selectedProvider]);

    const loadProviders = async () => {
        try {
            setLoadingProviders(true);
            const response = await fetchProviders({ typ: 'all', opr: 'PMT' });
            if (response && response.contents) {
                setProviders(response.contents);
                if (response.contents.length > 0) {
                    setSelectedProvider(response.contents[0]);
                }
            }
        } catch (error) {
            console.error('Error loading providers:', error);
        } finally {
            setLoadingProviders(false);
        }
    };

    const formatAccountNumber = (input) => {
        const raw = input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        let result = '';

        if (raw.length >= 2) {
            result += raw.substring(0, 2) + '-';
        } else {
            result += raw;
            return result;
        }

        if (raw.length >= 6) {
            result += raw.substring(2, 6) + '-';
        } else {
            result += raw.substring(2);
            return result;
        }

        result += raw.substring(6, 9);
        return result;
    };

    const handleAccountChange = (text) => {
        if (selectedProvider?.provider_type?.toLowerCase() === 'netikash') {
            const isDeleting = text.length < previousValue.current.length;
            if (!isDeleting) {
                text = formatAccountNumber(text);
            }
            previousValue.current = text;
            setAccount(text);
        } else {
            let cleaned = text.replace(/\D/g, '');
            
            if (selectedProvider?.provider_type?.toUpperCase() === 'MOBILE MONEY' && cleaned.startsWith('243')) {
                cleaned = cleaned.substring(3);
            }
            
            if (selectedProvider?.account_length && cleaned.length > selectedProvider.account_length) {
                cleaned = cleaned.substring(0, selectedProvider.account_length);
            }
            
            setAccount(cleaned);
        }
    };

    const handleProviderSelect = (provider) => {
        setSelectedProvider(provider);
        setAccount('');
        setAccountFormatError(false);
        previousValue.current = '';
    };

    const handleCreatePaymentRequest = async () => {
        try {
            const amountNum = normalizeAmountForApi(amount, formCurrency);
            if (amountNum == null || amountNum <= 0) {
                setFormError('Le montant est vide ou inférieur à zéro');
                return;
            }
            
            if (!selectedProvider || !selectedProvider.id || !account) {
                setFormError('Veuillez indiquer le numéro de compte et sélectionner un réseau');
                return;
            }

            if (!selectedOrganization?.id) {
                setFormError('Organisation non sélectionnée');
                return;
            }
        
            const freshList = await loadPosList(selectedPos?.id || null);
            const terminal = pickTerminalFromList(freshList, selectedPos?.id);

            if (!terminal?.id) {
                setFormError(
                    freshList.length === 0
                        ? 'Aucun terminal disponible. Créez un terminal dans Gestion des caisses.'
                        : 'Terminal non sélectionné. Veuillez sélectionner un POS.'
                );
                return;
            }

            if (terminal.id !== selectedPos?.id) {
                setSelectedPos(terminal);
            }

            setSubmitting(true);
            setFormError('');

            let cleanedAccount = account;
            let accountPrefix = null;
            
            if (selectedProvider.provider_type?.toLowerCase() === 'netikash') {
                cleanedAccount = account.replace(/-/g, '').toUpperCase();
                accountPrefix = null;
            } else if (selectedProvider.provider_type?.toUpperCase() === 'MOBILE MONEY') {
                cleanedAccount = account.replace(/\D/g, '');
                
                if (cleanedAccount.startsWith('243') && cleanedAccount.length > 3) {
                    cleanedAccount = cleanedAccount.substring(3);
                }
                
                if (cleanedAccount.length > 0) {
                    accountPrefix = '243';
                }
            }

            const paymentRequestData = {
                title: title || 'Règlement',
                amount: amountNum,
                currency: formCurrency,
                company_id: selectedOrganization.id,
                terminal: terminal.id,
                customer: null, // optionnel : si id fourni, le backend associe le client
                paymentMethod: {
                    prefix: accountPrefix,
                    provider_id: selectedProvider.id,
                    provider_name: selectedProvider.name,
                    suffix: null,
                    account: cleanedAccount,
                    full_name: null,
                    expiration_date: null,
                    control: null,
                    owner: null,
                }
            };

            console.log('[PaymentRequest] Envoi de la demande:', paymentRequestData);
            const response = await createPaymentRequest(paymentRequestData);
            
            if (response) {
                const { error: apiError, message, payload } = response;
                if (apiError) {
                    setFormError(message || 'Erreur lors de la création de la demande');
                    setSubmitting(false);
                    return;
                }

                const requestId = extractPaymentRequestId(response);
                const merged = { ...payload, request_id: requestId };
                setPaymentRequest(merged);
                setTitle('Règlement');
                setAmount('');
                setAccount('');
                setFormError('');

                window.dispatchEvent(new CustomEvent('paymentRequest:created', {
                    detail: { organizationId: selectedOrganization.id }
                }));

                /* Spinner + WebSocket comme sur mobile (flash) */
                if (requestId) {
                    setWaitingRequestId(requestId);
                    setWaitingModalOpen(true);
                }
            }
        } catch (error) {
            console.error('Error creating payment request:', error);
            // Améliorer les messages d'erreur pour l'utilisateur
            let errorMessage = 'Erreur lors de la création de la demande de paiement';
            if (error?.response?.status === 500) {
                errorMessage = 'Erreur serveur. Veuillez réessayer dans quelques instants.';
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message && !error.message.includes('Internal Server Error')) {
                errorMessage = error.message;
            }
            setFormError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <div className="min-h-screen bg-slate-50 flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="payment"
            />

            <div className="flex-1 min-w-0 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="min-h-screen p-4 sm:p-6">
                    <DashboardHeader
                        timeRange={timeRange}
                        onTimeRangeChange={setTimeRange}
                        currency={currency}
                        onCurrencyChange={setCurrency}
                        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                    />

                    {/* En-tête */}
                    <div className="mb-6 mt-3">
                        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                            <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent px-6 py-5">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                                        <Receipt className="text-white text-2xl" />
                                    </div>
                                    <div>
                                        <h1 className="text-2xl font-bold text-slate-800">Demandes de paiement</h1>
                                        <p className="text-slate-600 text-sm mt-0.5">Envoyez une demande de paiement directement sur le wallet de votre client</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
                            {/* Colonne gauche : formulaire */}
                            <div className="xl:col-span-2">
                            {/* Section Création de demande de paiement */}
                            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                                            <Receipt className="text-amber-600" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-slate-800">Nouvelle demande</h2>
                                            <p className="text-xs text-slate-500">Montant, réseau de paiement et numéro de compte</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-5">

                                {/* Montant */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-slate-700">Montant ({formCurrency})</label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const next = formCurrency === 'CDF' ? 'USD' : 'CDF';
                                                setFormCurrency(next);
                                                if (amount) setAmount(formatAmountInputDisplay(amount, next));
                                            }}
                                            className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                                        >
                                            {formCurrency === 'CDF' ? 'USD' : 'CDF'}
                                        </button>
                                    </div>
                                    <div className="flex space-x-2">
                                        <AmountInput
                                            value={amount}
                                            onChange={setAmount}
                                            currency={formCurrency}
                                            placeholder="Ex: 15000"
                                            className="flex-1 px-4 py-2.5 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500"
                                        />
                                        <div className="px-4 py-2.5 border border-amber-300 rounded-xl bg-amber-50 text-center min-w-[80px] flex items-center justify-center">
                                            {formCurrency}
                                        </div>
                                    </div>
                                </div>

                                {/* Message */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Message (Optionnel)</label>
                                    <textarea
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 min-h-[80px]"
                                        placeholder="Note pour le client"
                                    />
                                </div>

                                {/* Sélection des providers (réseaux de paiement) */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Réseau de paiement *</label>
                                    <ProviderPicker
                                        providers={providers}
                                        selectedId={selectedProvider?.id}
                                        onSelect={handleProviderSelect}
                                        loading={loadingProviders}
                                    />
                                </div>

                                {/* Champ de compte/téléphone */}
                                {selectedProvider && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            {selectedProvider.account_label || 'Numéro de compte'} *
                                        </label>
                                        <input
                                            ref={accountRef}
                                            type={selectedProvider.provider_type?.toLowerCase() === 'netikash' ? 'text' : 'tel'}
                                            inputMode={selectedProvider.provider_type?.toLowerCase() === 'netikash' ? 'text' : 'numeric'}
                                            autoCapitalize={selectedProvider.provider_type?.toLowerCase() === 'netikash' ? 'characters' : 'none'}
                                            autoComplete="off"
                                            autoCorrect="off"
                                            value={account}
                                            onChange={(e) => handleAccountChange(e.target.value)}
                                            className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${
                                                accountFormatError
                                                    ? 'border-rose-500 bg-rose-50'
                                                    : 'border-amber-300 hover:border-amber-400'
                                            }`}
                                            placeholder={selectedProvider.account_placeholder || 'Numéro de compte'}
                                            maxLength={selectedProvider.account_length ? 
                                                (selectedProvider.provider_type?.toLowerCase() === 'netikash' ? 
                                                    selectedProvider.account_length + 2 : 
                                                    selectedProvider.account_length) : 
                                                undefined}
                                        />
                                        {accountFormatError && (
                                            <p className="mt-1 text-sm text-rose-600">
                                                Veuillez utiliser le bon format du {selectedProvider.account_label}
                                            </p>
                                        )}
                                        {selectedProvider.provider_type?.toUpperCase() === 'MOBILE MONEY' && (
                                            <p className="mt-1 text-xs text-slate-500">
                                                Saisissez uniquement le numéro (ex: 999123456). Le préfixe +243 sera ajouté automatiquement.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Sélection du terminal */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <label className="block text-sm font-medium text-slate-700">
                                            Terminal de paiement *
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => loadPosList(selectedPos?.id || null)}
                                            disabled={loadingTerminals}
                                            className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800 disabled:opacity-50"
                                            title="Actualiser la liste des terminaux"
                                        >
                                            <Refresh className={loadingTerminals ? 'animate-spin' : ''} sx={{ fontSize: 16 }} />
                                            Actualiser
                                        </button>
                                    </div>
                                    <select
                                        value={selectedPos?.id || ''}
                                        onChange={(e) => {
                                            const pos = posList.find((p) => p.id === e.target.value);
                                            setSelectedPos(pos || null);
                                        }}
                                        disabled={loadingTerminals}
                                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 disabled:opacity-60"
                                        required
                                    >
                                        <option value="">
                                            {loadingTerminals
                                                ? 'Chargement des terminaux...'
                                                : posList.length === 0
                                                    ? 'Aucun terminal — créez-en un dans Gestion des caisses'
                                                    : 'Sélectionner un terminal'}
                                        </option>
                                        {posList.map((pos) => (
                                            <option key={pos.id} value={pos.id}>
                                                {formatTerminalOptionLabel(pos)}
                                            </option>
                                        ))}
                                    </select>
                                    {terminalsError && (
                                        <p className="mt-1.5 text-sm text-rose-600">{terminalsError}</p>
                                    )}
                                    {!loadingTerminals && !terminalsError && posList.length > 0 && (
                                        <p className="mt-1.5 text-xs text-slate-500">
                                            {posList.length}{' '}
                                            {posList.length > 1 ? 'terminaux disponibles' : 'terminal disponible'}
                                        </p>
                                    )}
                                </div>

                                {formError && (
                                    <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl text-sm">
                                        {formError}
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex space-x-3">
                                    <button
                                        onClick={handleCreatePaymentRequest}
                                        disabled={submitting || !amount || !selectedProvider || !account || !selectedPos}
                                        className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4 rounded-xl font-medium hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? (
                                            <div className="flex items-center justify-center space-x-2">
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                <span>Création...</span>
                                            </div>
                                        ) : (
                                            <>
                                                <QrCode className="inline mr-2" />
                                                Envoyer la demande
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTitle('Règlement');
                                            setAmount('');
                                            setAccount('');
                                            setFormError('');
                                            setPaymentRequest(null);
                                        }}
                                        className="px-6 py-3 bg-slate-500 text-white rounded-xl font-medium hover:bg-slate-600 transition-all duration-300 shadow-sm hover:shadow-md"
                                    >
                                        Effacer
                                    </button>
                                </div>
                                </div>
                            </div>

                            <WaitingPaymentModal
                                open={waitingModalOpen}
                                requestId={waitingRequestId}
                                amount={paymentRequest?.amount ?? (parseAmount(amount) || '—')}
                                currency={paymentRequest?.currency || formCurrency}
                                onClose={() => {
                                    setWaitingModalOpen(false);
                                    setWaitingRequestId(null);
                                }}
                                onApproved={() => {
                                    window.dispatchEvent(new CustomEvent('transaction:created', {
                                        detail: { organizationId: selectedOrganization?.id }
                                    }));
                                }}
                            />

                            {/* Confirmation après création */}
                            {paymentRequest && !waitingModalOpen && (
                                <div className="mt-6 bg-emerald-50/80 border border-emerald-200 rounded-2xl p-5 shadow-sm">
                                    <div className="flex items-start gap-4">
                                        <div className="w-11 h-11 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0 shadow-sm">
                                            <CheckCircle className="text-white text-xl" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="text-base font-semibold text-emerald-900 mb-1">Demande envoyée</h3>
                                            <p className="text-sm text-emerald-800/90 mb-4">
                                                La demande a été envoyée au client. Suivez la confirmation en direct via la fenêtre WebSocket (ou rouvrez une demande depuis la liste).
                                            </p>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                                <div className="bg-white/70 rounded-xl px-3 py-2 border border-emerald-100">
                                                    <span className="text-emerald-700/80 block text-xs font-medium uppercase tracking-wide">Montant</span>
                                                    <span className="font-semibold text-slate-900">{formatCurrency(paymentRequest?.amount ?? parseAmount(amount), paymentRequest?.currency || formCurrency)}</span>
                                                </div>
                                                <div className="bg-white/70 rounded-xl px-3 py-2 border border-emerald-100">
                                                    <span className="text-emerald-700/80 block text-xs font-medium uppercase tracking-wide">Client</span>
                                                    <span className="font-medium text-slate-800 truncate block">{paymentRequest?.customer?.name || paymentRequest?.paymentMethod?.account || account || '—'}</span>
                                                </div>
                                                <div className="bg-white/70 rounded-xl px-3 py-2 border border-emerald-100">
                                                    <span className="text-emerald-700/80 block text-xs font-medium uppercase tracking-wide">Réseau</span>
                                                    <span className="font-medium text-slate-800">{paymentRequest?.paymentMethod?.provider_name || selectedProvider?.name || '—'}</span>
                                                </div>
                                                <div className="bg-white/70 rounded-xl px-3 py-2 border border-emerald-100">
                                                    <span className="text-emerald-700/80 block text-xs font-medium uppercase tracking-wide">Valide {PAYMENT_REQUEST_VALIDITY_MINUTES} min</span>
                                                    <span className="font-medium text-slate-800">En attente</span>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setPaymentRequest(null)}
                                                className="mt-4 text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                                            >
                                                Créer une autre demande
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            </div>

                            {/* Colonne droite : aide */}
                            <PaymentRequestAside providers={providers} />
                        </div>
                    </div>
                </div>
                {/* Modal de gestion des terminaux */}
                <TerminalManagementModal
                    show={showTerminalModal}
                    onClose={() => setShowTerminalModal(false)}
                    posList={posList}
                />


            </div>
        </div>
    );
};

export default PaymentRequests;